import { Pipe, PipeTransform } from '@angular/core';
import { NgbPaginationNumber } from '@ng-bootstrap/ng-bootstrap';
import * as moment from 'moment';
import * as _ from 'underscore';
import { CAMPAIGN_STAGES, COUPON_TYPES, ORDER_STATUS, PRODUCT_FAMILY, PRODUCT_TYPES } from '../core/helpers/constants';
import { TranslateService } from '@ngx-translate/core';
import { IRole, IRoleCreation } from '../core/models/auth.models';

@Pipe({ name: 'P_StringSplit' })
export class P_StringSplit implements PipeTransform {
  transform(stringToSplit: string, splitChar: string, indexToReturn = 0): string {
    return stringToSplit.split(splitChar)[indexToReturn];
  }
}

@Pipe({ name: 'P_AppDateFormat', pure: false })
export class P_AppDateFormat implements PipeTransform {
  transform(dateToFormat: string | Date, format = '', toLocal = false): string {
    let r: any = '';
    if (dateToFormat == null) return r;

    // Handle MMYY format explicitly
    if (typeof dateToFormat === 'string' && dateToFormat.length === 4 && /^\d{4}$/.test(dateToFormat)) {
      r = moment(dateToFormat, 'MMYY'); // Parse as MMYY
    } else {
      r = moment(dateToFormat); // Default parsing
    }

    if (toLocal) {
      r = moment.utc(dateToFormat);
      r = r.local();
    }

    if (format) {
      r = r.format(format);
    } else {
      r = r.format('MM/DD/YYYY');
    }

    return r;
  }
}

@Pipe({ name: 'P_CampaignStageTitle', pure: false })
export class P_CampaignStageTitle implements PipeTransform {
  constructor(private translate: TranslateService) {}
  transform(id: any): string | undefined {
    if (id == null) return '';
    const stage = CAMPAIGN_STAGES.find(x => String(x.value) === String(id));
    if (!stage) return '';

    const translated = this.translate.instant(stage.translate);
    if (translated == null) {
      return stage.title || '';
    }

    const translatedStr = String(translated);
    const key = String(stage.translate);

    // If translation is missing, ngx-translate typically returns the key.
    // Also handle edge cases like key returned with whitespace or wrappers.
    const looksLikeMissing =
      translatedStr === key ||
      translatedStr.trim() === key ||
      translatedStr.includes(key);

    if (looksLikeMissing) {
      return stage.title || '';
    }

    return translatedStr;
  }
}

@Pipe({ name: 'P_CampaignPlatformTitle', pure: false })
export class P_CampaignPlatformTitle implements PipeTransform {
  constructor(private translate: TranslateService) {}

  transform(value: string | null | undefined, isAltLayout = false): string {
    if (value == null) return '';

    const raw = String(value);
    const keyByPlatform: { [key: string]: string } = {
      'Campaign Consulting': 'PLATFORMS.CAMPAIGN_CONSULTING',
      Facebook: isAltLayout ? 'PLATFORMS.META' : 'PLATFORMS.FACEBOOK',
      Google: 'PLATFORMS.GOOGLE',
      'National Add On': 'PLATFORMS.NATIONAL_ADD_ON',
      Other: 'PLATFORMS.OTHER',
      TikTok: 'PLATFORMS.TIKTOK',
      Website: 'PLATFORMS.WEBSITE',
      YouTube: 'PLATFORMS.YOUTUBE'
    };
    const key = keyByPlatform[raw];
    if (!key) return raw;

    const translated = this.translate.instant(key);
    return !translated || translated === key ? raw : translated;
  }
}

@Pipe({ name: 'P_CampaignStudioStatus', pure: false })
export class P_CampaignStudioStatus implements PipeTransform {
  constructor(private translate: TranslateService) {}

  transform(value: string | null | undefined): string {
    if (value == null) return '';

    const raw = String(value);
    const normalized = raw.trim().toLowerCase();

    let key: string | null = null;
    switch (normalized) {
      case 'sustained':
        key = 'CAMPAIGN_STUDIO_STATUS.SUSTAINED';
        break;
      case 'presale':
        key = 'CAMPAIGN_STUDIO_STATUS.PRESALE';
        break;
      default:
        return raw;
    }

    const translated = this.translate.instant(key);
    // If translation is missing, fall back to the stored DB value
    if (!translated || translated === key) {
      return raw;
    }
    return translated;
  }
}

@Pipe({ name: 'P_SubscriptionStatus', pure: false })
export class P_SubscriptionStatus implements PipeTransform {
  constructor(private translate: TranslateService) {}

  transform(value: string | null | undefined): string {
    if (value == null) return '';

    const raw = String(value);
    const normalized = raw.trim().toLowerCase();
    const keyByStatus: { [key: string]: string } = {
      active: 'ACTIVE',
      canceled: 'CANCELED',
      'payment error': 'PAYMENT_ERROR',
      'payment pending': 'PAYMENT_PENDING',
      no_active_subscription: 'NO_ACTIVE_SUBSCRIPTION',
      'no active subscription': 'NO_ACTIVE_SUBSCRIPTION'
    };
    const key = keyByStatus[normalized] || raw;
    const translated = this.translate.instant(key);

    return !translated || translated === key ? raw : translated;
  }
}

@Pipe({ name: 'P_TierName', pure: false })
export class P_TierName implements PipeTransform {
  constructor(private translate: TranslateService) {}

  transform(value: string | null | undefined): string {
    if (value == null) return '';

    const raw = String(value);
    const normalized = raw.trim().toLowerCase();

    let key: string | null = null;
    switch (normalized) {
      case 'basic tier':
        key = 'TIERS.BASIC_TIER';
        break;
      case 'standard tier':
        key = 'TIERS.STANDARD_TIER';
        break;
      case 'premium tier':
        key = 'TIERS.PREMIUM_TIER';
        break;
      default:
        return raw;
    }

    const translated = this.translate.instant(key);
    if (!translated || translated === key) {
      return raw;
    }
    return translated;
  }
}

@Pipe({ name: 'P_CouponTypesTitle', pure: false })
export class P_CouponTypesTitle implements PipeTransform {
  constructor(private translate: TranslateService) {}

  transform(id: any): string | undefined {
    if (id == null)
      return '';

    const titleKey = COUPON_TYPES.find(x => x.value === id)?.title;
    if (!titleKey) return '';

    const translated = this.translate.instant(titleKey);
    return (translated && translated !== titleKey) ? translated : titleKey.split('.').pop()?.replace(/_/g, ' ') ?? titleKey;
  }
}

@Pipe({ name: 'P_CouponExpiry', pure: false })
export class P_CouponExpiry implements PipeTransform {
  transform(dateToFormat: Date, format = '', toLocal = false): string {
    let r: any = '';
    if (dateToFormat == null)
      return "No Expiry";
    r = moment(dateToFormat);
    if (toLocal) {
      r = moment.utc(dateToFormat);
      r = r.local();
    }
    if (format) {
      r = r.format(format);
    }
    else
      r = r.format("MM/DD/YYYY");
    return r;
  }
}

@Pipe({ name: 'P_Boolean', pure: false })
export class P_Boolean implements PipeTransform {
  constructor(private translate: TranslateService) {}
  transform(value: any): string | undefined {
    if (value == null) return '';
    return value === true
      ? this.translate.instant('COMMON.YES')
      : this.translate.instant('COMMON.NO');
  }
}

@Pipe({ name: 'P_RolesType_Filter', pure: false })
export class P_RolesType_Filter implements PipeTransform {
  transform(value: Array<IRole>, type: number): Array<IRole> | undefined {
    let r: Array<IRole> = [];
    if (value == null || value.length == 0)
      return r;
    return value.filter(x => x.type === type);
  }
}

@Pipe({ name: 'P_ProducType', pure: false })
export class P_ProducType implements PipeTransform {
  constructor(private translate: TranslateService) {}

  transform(type: number): string | undefined {
    const translateProductType = (key: string, fallback: string): string => {
      const translated = this.translate.instant(key);
      if (!translated || translated === key) {
        return fallback;
      }
      return translated;
    };

    switch (type) {
      case PRODUCT_TYPES.SimpleProduct.code:
        return translateProductType(PRODUCT_TYPES.SimpleProduct.title, 'Simple Product');
      case PRODUCT_TYPES.SimpleSubscription.code:
        return translateProductType(PRODUCT_TYPES.SimpleSubscription.title, 'Simple Subscription');
      case PRODUCT_TYPES.VariableSubscription.code:
        return translateProductType(PRODUCT_TYPES.VariableSubscription.title, 'Variable Subscription');
      default:
        return translateProductType('BUNDLE', 'Bundle');
    }
  }
}

@Pipe({ name: 'P_ProducFamily', pure: false })
export class P_ProducFamily implements PipeTransform {
  transform(type: number): string {
    switch (type) {
      case PRODUCT_FAMILY.Bundle:
        return "Bundle";
      case PRODUCT_FAMILY.AdSpend:
        return "Ad Spend";
      case PRODUCT_FAMILY.DigitalMarketing:
        return "Digital Marketing";
      case PRODUCT_FAMILY.CampaignConsulting:
        return "Campaign Consulting";
      case PRODUCT_FAMILY.HostedWebsite:
        return "Hosted Website";
      case PRODUCT_FAMILY.SEOProgram:
        return "SEO Program";
      case PRODUCT_FAMILY.SocialMedia:
        return "Social Media";
      case PRODUCT_FAMILY.AddPlatform:
        return "Add Platform";
      default:
        return "";
    }

  }
}

@Pipe({ name: 'P_CallbackPipe', pure: false })
export class P_CallbackPipe implements PipeTransform {
  transform(items: any[], callback: (item: any) => boolean): any {
    if (!items || !callback) {
      return items;
    }
    return items.filter(item => callback(item));
  }
}

@Pipe({ name: 'P_OrderStatus', pure: false })
export class P_OrderStatus implements PipeTransform {
  constructor(private translate: TranslateService) {}

  transform(id: any): string {
    if (id == null) return '';

    const statusKeys: { [key: number]: string } = {
      0: 'PENDING',
      1: 'PAYMENT_PENDING',
      2: 'CANCELED',
      3: 'COMPLETED',
      4: 'FULLY_REFUNDED',
      5: 'PARTIALLY_REFUNDED'
    };
    const key = statusKeys[Number(id)];
    if (!key) return ORDER_STATUS.find(x => x.value === id)?.title || String(id);

    const translated = this.translate.instant(key);
    return !translated || translated === key ? ORDER_STATUS.find(x => x.value === id)?.title || String(id) : translated;
  }
}

@Pipe({ name: 'P_FreqFromDuration', pure: false })
export class P_FreqFromDuration implements PipeTransform {
  transform(dur: string | undefined): string | undefined {
    switch (dur) {
      case 'day':
        return '';
      case 'week':
        return 'day';
      case 'month':
        return 'week';
      case 'year':
        return 'month';
      default:
        return ''
    }
  }
}

@Pipe({ name: 'P_Ordinal', pure: false })
export class P_Ordinal implements PipeTransform {
  transform(value: number): string {
    if (value === 1) {
      return value + 'st';
    } else if (value === 2) {
      return value + 'nd';
    } else if (value === 3) {
      return value + 'rd';
    } else {
      return value + 'th';
    }
  }
}

@Pipe({ name: 'P_RolesName_Filter', pure: false })
export class P_RolesName_Filter implements PipeTransform {
  constructor(private translate: TranslateService) {}

  transform(code: number): string {
    const keyByCode: Record<number, string> = {
      1: 'ROLES.NAMES.ADMIN',
      2: 'ROLES.NAMES.TECH_SERVICES',
      3: 'ROLES.NAMES.DMS',
      4: 'ROLES.NAMES.SHOP_MANAGER',
      5: 'ROLES.NAMES.COMPANY_OWNER',
      6: 'ROLES.NAMES.MARKETING_MANAGER',
      7: 'ROLES.NAMES.CLIENT_OWNER',
      8: 'ROLES.NAMES.EMPLOYEE',
      9: 'ROLES.NAMES.SUBSCRIPTION_EMPLOYEE',
      10: 'ROLES.NAMES.SALES_REP',
      12: 'ROLES.NAMES.STAFF'
    };

    const key = keyByCode[code];
    if (!key) return '';
    const translated = this.translate.instant(key);
    return (translated && translated !== key) ? translated : key;

  }
}

@Pipe({ name: 'cleanupCampaign' })
export class CleanupCampaignPipe implements PipeTransform {
  transform(value: string): string {
    if (!value) return value;
    return value.replace(' FB', '').replace(' TT', '');
  }
}

@Pipe({ name: 'NumberPipe', pure: false })
export class NumberPipe implements PipeTransform {
  transform(value: number, digits: number[] = [0]): string {
    let fractionDigits = Number(digits[0]);
    if (fractionDigits > 0) {
      return value.toLocaleString('en-US', { style:'decimal', minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits });
    }
    return value.toLocaleString('en-US');
  }
}

@Pipe({
  name: 'PercentPipe', pure: false
})
export class PercentPipe implements PipeTransform {
  transform(value: number, digits: number[] = [2]): string {
    return (value * 100).toFixed(digits[0]) + '%';
  }
}
